import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import { ThreadModel } from '@flowwai-frontend/shared-web/ai-chat-support/data-access'
import {
   InfiniteScrollList,
   Loader
} from '@flowwai-frontend/shared/common-components'

import { CHAT_MESSAGES_CONTAINER_ID } from '../../constants/ChatBoxConstants'

import AIChatReply from '../AIChatReply/AIChatReply'
import UserQueriedChatBox from '../UserQueriedChatBox/UserQueriedChatBox'

import './index.css'
import * as Styles from './styles'

interface Props {
   threadModel: ThreadModel
   hasMoreMessagesToFetch: boolean
   fetchMoreMessages: () => void
}

const ThreadDetailedViewBody = (props: Props): React.ReactElement => {
   const { threadModel, hasMoreMessagesToFetch, fetchMoreMessages } = props

   const renderSystemLoadingMessage = (): React.ReactNode =>
      threadModel.loadingMessageStatus ? (
         <div className='pb-6'>
            <AIChatReply
               messageModel={threadModel.loadingMessage}
               shouldAnimateText
               loadingMessageStatus={threadModel.loadingMessageStatus}
               isLoading={
                  threadModel.loadingMessageStatus !== 'CONTENT_STREAMING'
               }
            />
         </div>
      ) : null

   const renderLoader = (): React.ReactElement => (
      <div className={'p-[24px] flex items-center justify-center'}>
         <Loader height={20} width={20} />
      </div>
   )

   const renderChatMessages = (): React.ReactElement => (
      <div
         className={Styles.infiniteScrollListContainerStyles}
         id={CHAT_MESSAGES_CONTAINER_ID}
      >
         <InfiniteScrollList
            dataLength={threadModel.messages.length}
            hasMore={hasMoreMessagesToFetch}
            loader={renderLoader()}
            next={fetchMoreMessages}
            inverse={true}
            className='ai-chat-support-infinite-scroll-container'
            scrollThreshold={0.6}
            scrollableTarget={CHAT_MESSAGES_CONTAINER_ID}
            style={{ display: 'flex', flexDirection: 'column-reverse' }}
         >
            <div className={Styles.infiniteScrollListInnerContainerStyles}>
               {threadModel.messages
                  .slice()
                  .reverse()
                  .map(message => {
                     const isUserMessage = message.type === 'QUESTION'

                     const stringifiedMessageId = `MESSAGE:${message.messageId}`

                     return (
                        <div
                           className={cn('pb-4 max-w-full', {
                              'ml-auto': isUserMessage
                           })}
                           id={stringifiedMessageId}
                           key={stringifiedMessageId}
                        >
                           {isUserMessage ? (
                              <UserQueriedChatBox
                                 userSearchQuery={message.content}
                                 onUpdateSearchQuery={() => null}
                                 timeStamp={message.timeStamp}
                              />
                           ) : (
                              <AIChatReply messageModel={message} />
                           )}
                        </div>
                     )
                  })}

               {renderSystemLoadingMessage()}
            </div>
         </InfiniteScrollList>
      </div>
   )

   return renderChatMessages()
}

export default observer(ThreadDetailedViewBody)
